import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SealCheck, Plus, Trash, X, Image as ImageIcon, MagnifyingGlass,
  CircleNotch, Clock, WarningCircle, CheckCircle, Receipt as ReceiptIcon, CaretRight, FilePdf, Package, Check,
} from "@phosphor-icons/react";
import api, { API, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, longDate, shortDate } from "@/lib/format";
import { Card, Loading, EmptyState, MerchantLogo } from "@/components/riq";

export const WARRANTY_PERIODS = [
  { m: 3, l: "3 months" }, { m: 6, l: "6 months" }, { m: 12, l: "1 year" },
  { m: 24, l: "2 years" }, { m: 36, l: "3 years" }, { m: 60, l: "5 years" },
];

export function periodLabel(m) {
  return WARRANTY_PERIODS.find((p) => p.m === m)?.l || `${m} months`;
}

export default function Warranties() {
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [imgPath, setImgPath] = useState(null);

  const load = () => api.get("/warranties").then((r) => setData(r.data)).catch(() => setData({ warranties: [], counts: {} }));
  useEffect(() => { load(); }, []);

  const remove = async (w) => {
    if (!window.confirm(`Remove warranty tracking for "${w.label}"?`)) return;
    try { await api.delete(`/warranties/${w.id}`); toast.success("Warranty removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const changePeriod = async (w, months) => {
    try { await api.patch(`/warranties/${w.id}`, { period_months: months }); toast.success("Warranty period updated"); load(); }
    catch { toast.error("Could not update"); }
  };

  if (data === null) return <Loading label="Loading your warranties…" />;
  const { warranties = [], counts = {} } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Buyer protection</div>
          <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-riq-primary mt-1">Warranties</h1>
        </div>
        <button data-testid="add-warranty-btn" onClick={() => setAddOpen(true)} className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-4 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform shrink-0">
          <Plus size={18} weight="bold" /> Add warranty
        </button>
      </div>

      {warranties.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Active" value={counts.active || 0} color="#22B573" />
          <StatCard label="Expiring soon" value={counts.expiring_soon || 0} color="#B25E00" testId="warranty-expiring-count" />
          <StatCard label="Expired" value={counts.expired || 0} color="#FF6B6B" />
        </div>
      )}

      {warranties.length === 0 ? (
        <EmptyState
          icon={SealCheck}
          title="No warranties tracked yet"
          subtitle="Add a warranty against any receipt and we'll track the expiry date and keep the original till slip attached."
        />
      ) : (
        <div className="space-y-3" data-testid="warranty-list">
          {warranties.map((w) => (
            <WarrantyCard key={w.id} w={w} cur={cur} onView={() => w.storage_path && setImgPath(w.storage_path)} onRemove={() => remove(w)} onPeriod={(m) => changePeriod(w, m)} />
          ))}
        </div>
      )}

      {addOpen && <AddWarrantyModal cur={cur} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}

      {imgPath && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setImgPath(null)}>
          <img data-testid="warranty-slip-image" src={`${API}/files/${imgPath}`} alt="Original till slip" className="max-h-[90vh] rounded-xl" />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, testId }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold mt-1" style={{ color }} data-testid={testId}>{value}</div>
    </Card>
  );
}

function StatusPill({ status, days }) {
  if (status === "expired") {
    return <span className="rounded-full bg-[#FFECEC] text-[#FF6B6B] px-3 py-1 text-xs font-semibold flex items-center gap-1"><WarningCircle size={13} weight="fill" /> Expired</span>;
  }
  if (status === "expiring_soon") {
    return <span className="rounded-full bg-riq-gold/25 text-[#B25E00] px-3 py-1 text-xs font-semibold flex items-center gap-1"><Clock size={13} weight="fill" /> Expires in {Math.max(days, 0)}d</span>;
  }
  return <span className="rounded-full bg-[#E4F7ED] text-[#22B573] px-3 py-1 text-xs font-semibold flex items-center gap-1"><CheckCircle size={13} weight="fill" /> {days} days left</span>;
}

function WarrantyCard({ w, cur, onView, onRemove, onPeriod }) {
  const [editing, setEditing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const claimPack = async () => {
    setDownloading(true);
    try {
      await downloadFile(`/warranties/${w.id}/claim-pack`, `claim_pack_${(w.label || "warranty").replace(/[^A-Za-z0-9]+/g, "_")}.pdf`);
      toast.success("Claim pack downloaded");
    } catch { toast.error("Could not generate the claim pack"); }
    finally { setDownloading(false); }
  };
  return (
    <Card data-testid={`warranty-card-${w.id}`} className="p-4">
      <div className="flex items-start gap-3">
        <MerchantLogo name={w.merchant_name} size={44} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-head font-semibold text-riq-primary truncate">{w.label}</div>
              <div className="text-xs text-muted-foreground truncate">{w.merchant_name} · bought {shortDate(w.start_date)} · {money(w.receipt_total, w.currency || cur)}</div>
            </div>
            <StatusPill status={w.status} days={w.days_until} />
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500 flex-wrap">
            <span className="rounded-full bg-riq-primary/10 text-riq-primary px-2.5 py-1 font-medium">{w.item_name ? "Item" : "Whole slip"}</span>
            <span className="rounded-full bg-secondary px-2.5 py-1">{periodLabel(w.period_months)} cover</span>
            <span>Expires {longDate(w.end_date)}</span>
          </div>

          {editing && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`warranty-period-edit-${w.id}`}>
              {WARRANTY_PERIODS.map((p) => (
                <button key={p.m} onClick={() => { onPeriod(p.m); setEditing(false); }} className={`rounded-full px-3 py-1.5 text-xs font-medium ${p.m === w.period_months ? "bg-riq-primary text-white" : "bg-secondary text-neutral-600 hover:bg-secondary/70"}`}>{p.l}</button>
              ))}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {w.has_image && (
              <button data-testid={`warranty-view-slip-${w.id}`} onClick={onView} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs hover:border-riq-primary"><ImageIcon size={14} weight="duotone" /> View till slip</button>
            )}
            <button data-testid={`warranty-claim-pack-${w.id}`} onClick={claimPack} disabled={downloading} className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-3 py-1.5 text-xs disabled:opacity-60">{downloading ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="fill" />} Claim pack</button>
            <button data-testid={`warranty-edit-period-${w.id}`} onClick={() => setEditing((v) => !v)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs hover:border-riq-primary"><Clock size={14} weight="duotone" /> Change period</button>
            <button data-testid={`warranty-remove-${w.id}`} onClick={onRemove} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs text-destructive hover:border-destructive ml-auto"><Trash size={14} /> Remove</button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function AddWarrantyModal({ cur, onClose, onSaved }) {
  const [receipts, setReceipts] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemId, setItemId] = useState(null);
  const [months, setMonths] = useState(12);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/receipts?limit=200").then((r) => setReceipts(r.data.receipts || [])).catch(() => setReceipts([]));
  }, []);

  const filtered = (receipts || []).filter((r) => `${r.merchant_name} ${r.date}`.toLowerCase().includes(q.toLowerCase()));

  const pick = async (r) => {
    setSelected(r);
    setItemId(null);
    setLabel(r.merchant_name || "");
    setItemsLoading(true);
    try { const d = await api.get(`/receipts/${r.id}`); setItems(d.data.items || []); }
    catch { setItems([]); }
    finally { setItemsLoading(false); }
  };

  const selectItem = (val) => {
    setItemId(val);
    const it = items.find((x) => x.id === val);
    setLabel(it ? (it.canonical_name || it.description || "") : (selected?.merchant_name || ""));
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post("/warranties", { receipt_id: selected.id, item_id: itemId || undefined, period_months: months, label });
      toast.success("Warranty added");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add warranty");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-y-auto safe-pb" onClick={(e) => e.stopPropagation()} data-testid="add-warranty-modal">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-riq-border">
          <h3 className="font-head font-bold text-riq-primary">{selected ? "What's covered?" : "Choose the receipt"}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X size={22} /></button>
        </div>

        {!selected ? (
          <div className="p-5">
            <div className="relative mb-4">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="warranty-receipt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search receipts…" className="w-full bg-secondary/50 rounded-full border border-riq-border pl-11 pr-4 py-3 text-sm outline-none focus:border-riq-primary" />
            </div>
            {receipts === null ? <Loading /> : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No receipts found. Scan a receipt first.</div>
            ) : (
              <div className="divide-y divide-riq-border -mx-1">
                {filtered.map((r) => (
                  <button key={r.id} data-testid={`warranty-pick-${r.id}`} onClick={() => pick(r)} className="w-full flex items-center gap-3 px-1 py-3 text-left hover:bg-secondary/40 rounded-lg">
                    <MerchantLogo name={r.merchant_name} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{r.merchant_name}</div>
                      <div className="text-xs text-muted-foreground">{shortDate(r.date)} · {money(r.total, r.currency || cur)}</div>
                    </div>
                    <CaretRight size={16} className="text-neutral-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <button onClick={() => setSelected(null)} className="text-sm text-neutral-500 flex items-center gap-1.5"><ReceiptIcon size={16} /> Change receipt</button>
            <Card className="p-4 flex items-center gap-3 bg-secondary/40">
              <MerchantLogo name={selected.merchant_name} size={40} />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{selected.merchant_name}</div>
                <div className="text-xs text-muted-foreground">{longDate(selected.date)} · {money(selected.total, selected.currency || cur)}</div>
              </div>
            </Card>

            <div>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Which item is under warranty?</span>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Warranties usually cover <span className="font-medium text-neutral-700">one product</span> (like a TV or kettle). Tap the exact item below so your claim pack shows the right thing — don't just cover the whole slip.</p>
              {itemsLoading ? (
                <div className="py-6 flex justify-center"><CircleNotch size={22} className="animate-spin text-riq-primary" /></div>
              ) : items.length === 0 ? (
                <div className="rounded-xl bg-secondary/50 border border-riq-border p-3 text-xs text-muted-foreground">We couldn't read individual items on this receipt, so we'll cover the whole receipt.</div>
              ) : (
                <div className="space-y-2 max-h-[38vh] overflow-y-auto -mx-1 px-1" data-testid="warranty-item-list">
                  {items.map((it) => {
                    const on = itemId === it.id;
                    return (
                      <button key={it.id} data-testid={`warranty-item-${it.id}`} onClick={() => selectItem(it.id)}
                        className={`w-full flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${on ? "border-riq-primary bg-riq-primary/5 ring-1 ring-riq-primary/30" : "border-riq-border hover:border-riq-primary/40"}`}>
                        <span className={`grid place-items-center h-8 w-8 rounded-lg shrink-0 ${on ? "bg-riq-primary text-white" : "bg-secondary text-neutral-400"}`}>
                          {on ? <Check size={16} weight="bold" /> : <Package size={16} weight="duotone" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-sm truncate">{it.canonical_name || it.description}</span>
                          {(it.total_price != null || it.description) && <span className="block text-xs text-muted-foreground truncate">{it.total_price != null ? money(it.total_price, selected.currency || cur) : it.description}</span>}
                        </span>
                        <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${on ? "border-riq-primary bg-riq-primary" : "border-neutral-300"}`} />
                      </button>
                    );
                  })}
                  <button data-testid="warranty-item-whole" onClick={() => selectItem("")}
                    className={`w-full flex items-center gap-3 rounded-xl border border-dashed px-3 py-3 text-left transition-colors ${itemId === "" ? "border-riq-primary bg-riq-primary/5 ring-1 ring-riq-primary/30" : "border-riq-border hover:border-riq-primary/40"}`}>
                    <span className={`grid place-items-center h-8 w-8 rounded-lg shrink-0 ${itemId === "" ? "bg-riq-primary text-white" : "bg-secondary text-neutral-400"}`}>
                      {itemId === "" ? <Check size={16} weight="bold" /> : <ReceiptIcon size={16} weight="duotone" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-sm">Whole receipt (all items)</span>
                      <span className="block text-xs text-muted-foreground">Only if the cover applies to everything on the slip.</span>
                    </span>
                    <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${itemId === "" ? "border-riq-primary bg-riq-primary" : "border-neutral-300"}`} />
                  </button>
                </div>
              )}
            </div>

            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Item / product name</span>
              <input data-testid="warranty-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Samsung TV" className="mt-1.5 w-full bg-white rounded-xl border border-riq-border px-3.5 py-2.5 text-sm outline-none focus:border-riq-primary" />
              <span className="block text-xs text-muted-foreground mt-1">Auto-filled from your choice — edit it to match the product on your warranty.</span>
            </label>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Warranty period</div>
              <div className="grid grid-cols-3 gap-2" data-testid="warranty-period-picker">
                {WARRANTY_PERIODS.map((p) => (
                  <button key={p.m} data-testid={`warranty-period-${p.m}`} onClick={() => setMonths(p.m)} className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${months === p.m ? "bg-riq-primary text-white border-riq-primary" : "bg-white text-neutral-600 border-riq-border hover:border-riq-primary"}`}>{p.l}</button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Cover runs from the purchase date ({shortDate(selected.date)}).</p>
            </div>

            <button data-testid="warranty-save-btn" disabled={saving} onClick={save} className="w-full rounded-full bg-riq-primary text-white py-3 font-medium disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <CircleNotch size={18} className="animate-spin" /> : <SealCheck size={18} weight="fill" />} Add warranty
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
